import React, { useState, useEffect } from 'react';
import {
  Building,
  Globe,
  Mail,
  Phone,
  MapPin,
  Headphones,
  DollarSign,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import api from '../utils/api';

const CompanyProfile = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const [companyData, setCompanyData] = useState({
    company_name: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    support_email: '',
    sales_email: ''
  });

  const [originalData, setOriginalData] = useState({});

  useEffect(() => {
    loadCompanyProfile();
  }, []);

  const loadCompanyProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get('/custom-values');

      // Extract company information from custom values
      const customValues = response.data || [];
      const companyInfo = {};

      // Map backend variable names to frontend field names
      const backendToFrontendMapping = {
        'company_name': 'company_name',
        'company_website': 'website',
        'company_email': 'email',
        'company_phone': 'phone',
        'company_address': 'address',
        'support_email': 'support_email',
        'sales_email': 'sales_email'
      };

      // Extract existing values from custom_values
      customValues.forEach(value => {
        const frontendField = backendToFrontendMapping[value.variable_name];
        if (frontendField) {
          companyInfo[frontendField] = value.value || '';
        }
      });

      // Fill in any missing fields with empty strings
      const allFrontendFields = ['company_name', 'website', 'email', 'phone', 'address', 'support_email', 'sales_email'];
      allFrontendFields.forEach(field => {
        if (!companyInfo[field]) {
          companyInfo[field] = '';
        }
      });

      setCompanyData(companyInfo);
      setOriginalData({ ...companyInfo });

    } catch (error) {
      console.error('Error loading company profile:', error);
      setErrors({ load: 'Failed to load company profile. Please refresh the page.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
    
    // Clear any existing errors for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear save success message when user starts editing
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!companyData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    
    if (companyData.email && !isValidEmail(companyData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (companyData.support_email && !isValidEmail(companyData.support_email)) {
      newErrors.support_email = 'Please enter a valid support email address';
    }
    
    if (companyData.sales_email && !isValidEmail(companyData.sales_email)) {
      newErrors.sales_email = 'Please enter a valid sales email address';
    }
    
    if (companyData.website && !isValidUrl(companyData.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      // Get current custom values to find existing IDs
      const currentResponse = await api.get('/custom-values');

      const existingValues = currentResponse.data || [];

      // Update each company field
      const updatePromises = Object.entries(companyData).map(async ([field, value]) => {
        // Map frontend field to backend variable name
        const variableNameMapping = {
          'company_name': 'company_name',
          'website': 'company_website',
          'email': 'company_email',
          'phone': 'company_phone',
          'address': 'company_address',
          'support_email': 'support_email',
          'sales_email': 'sales_email'
        };
        
        const backendVariableName = variableNameMapping[field];
        const existingValue = existingValues.find(v => v.variable_name === backendVariableName);
        
        if (existingValue) {
          // Update existing value
          return api.put(`/custom-values/${existingValue.id}`, {
            name: getFieldDisplayName(field),
            value: value,
            description: getFieldDescription(field)
          });
        } else {
          // Create new value
          return api.post('/custom-values', {
            name: getFieldDisplayName(field),
            variable_name: backendVariableName,
            value: value,
            description: getFieldDescription(field),
            type: 'text',
            required: field === 'company_name'
          });
        }
      });

      await Promise.all(updatePromises);

      // Update original data to reflect saved state
      setOriginalData({ ...companyData });
      setSaveSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (error) {
      console.error('Error saving company profile:', error);
      setErrors({ 
        save: error.response?.data?.detail || 'Failed to save company profile. Please try again.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setCompanyData({ ...originalData });
    setErrors({});
    setSaveSuccess(false);
  };

  const getFieldDisplayName = (field) => {
    const names = {
      company_name: 'Company Name',
      website: 'Company Website',
      email: 'Company Email', 
      phone: 'Company Phone',
      address: 'Company Address',
      support_email: 'Support Email',
      sales_email: 'Sales Email'
    };
    return names[field] || field;
  };

  const getFieldDescription = (field) => {
    const descriptions = {
      company_name: 'Your organization name',
      website: 'Your organization website URL',
      email: 'Primary company email address',
      phone: 'Primary company phone number', 
      address: 'Company physical address',
      support_email: 'Customer support email address',
      sales_email: 'Sales inquiries email address'
    };
    return descriptions[field] || '';
  };

  const hasChanges = () => {
    return JSON.stringify(companyData) !== JSON.stringify(originalData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading company profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Building className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
        </div>
        <p className="text-gray-600">
          Manage your organization's basic information. These details will be used throughout the system 
          for branding, communications, and templates.
        </p>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-800 font-medium">Company profile updated successfully!</p>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {errors.load && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{errors.load}</p>
          </div>
        </div>
      )}

      {errors.save && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{errors.save}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-1" />
                  Company Name *
                </label>
                <input
                  type="text"
                  value={companyData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.company_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your company name"
                />
                {errors.company_name && (
                  <p className="text-red-600 text-sm mt-1">{errors.company_name}</p>
                )}
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Website
                </label>
                <input
                  type="url"
                  value={companyData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.website ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="https://yourcompany.com"
                />
                {errors.website && (
                  <p className="text-red-600 text-sm mt-1">{errors.website}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={companyData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Primary Email
                </label>
                <input
                  type="email"
                  value={companyData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="info@yourcompany.com"
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Support Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Headphones className="w-4 h-4 inline mr-1" />
                  Support Email
                </label>
                <input
                  type="email"
                  value={companyData.support_email}
                  onChange={(e) => handleInputChange('support_email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.support_email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="support@yourcompany.com"
                />
                {errors.support_email && (
                  <p className="text-red-600 text-sm mt-1">{errors.support_email}</p>
                )}
              </div>

              {/* Sales Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  Sales Email
                </label>
                <input
                  type="email"
                  value={companyData.sales_email}
                  onChange={(e) => handleInputChange('sales_email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.sales_email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="sales@yourcompany.com"
                />
                {errors.sales_email && (
                  <p className="text-red-600 text-sm mt-1">{errors.sales_email}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Address</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Company Address
              </label>
              <textarea
                value={companyData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                placeholder="123 Business Street&#10;Suite 100&#10;City, State 12345"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              * Required fields. Changes will be applied throughout the system.
            </p>
            
            <div className="flex items-center space-x-3">
              {hasChanges() && (
                <button
                  onClick={handleReset}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Reset</span>
                </button>
              )}
              
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges()}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;